import { useCallback, useId, useMemo, useRef, useState } from 'react';
import { emptyValuesFor, validateAll, validateField } from '@srichaitra/validation';

/**
 * The form engine every form in the application uses.
 *
 * One hook, one validation library, one set of timing rules — so no two screens
 * disagree about when an error appears. The timing is the part worth stating,
 * because it is the difference between a form that helps and one that nags:
 *
 *   - Typing in a field that has never been left alone shows nothing. Telling
 *     somebody their email is invalid while they are on the third character is
 *     just noise.
 *   - Leaving a field (blur) validates that field only.
 *   - Submitting validates everything and reveals every error at once.
 *   - After a failed submit, typing re-validates live, because at that point
 *     the user has been told what is wrong and is trying to fix it.
 *
 * `fieldProps(name)` returns exactly the props the @srichaitra/ui controls
 * take, including the aria-describedby wiring, so a field is accessible by
 * construction rather than by each screen remembering to do it.
 */
export function useForm({ schema, initialValues, onSubmit }) {
  const baseId = useId();

  const defaults = useMemo(
    () => initialValues ?? emptyValuesFor(schema),
    [initialValues, schema]
  );

  const [values, setValues] = useState(defaults);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);

  /** Whether a submit has been attempted, which switches on live validation. */
  const submitAttempted = useRef(false);

  const setFieldValue = useCallback(
    (name, value) => {
      setValues((current) => {
        const next = { ...current, [name]: value };

        if (submitAttempted.current) {
          // Re-check this field against the *next* values, not the stale ones
          // still in state — otherwise the error always trails a keystroke.
          validateField(schema, name, next).then((message) => {
            setErrors((currentErrors) => ({ ...currentErrors, [name]: message ?? undefined }));
          });
        }

        return next;
      });
    },
    [schema]
  );

  const handleChange = useCallback(
    (event) => {
      const { name, type, checked, value } = event.target;
      setFieldValue(name, type === 'checkbox' ? checked : value);
    },
    [setFieldValue]
  );

  const handleBlur = useCallback(
    async (event) => {
      const { name } = event.target;
      setTouched((current) => ({ ...current, [name]: true }));

      const message = await validateField(schema, name, values);
      setErrors((current) => ({ ...current, [name]: message ?? undefined }));
    },
    [schema, values]
  );

  const handleSubmit = useCallback(async () => {
    submitAttempted.current = true;

    const result = await validateAll(schema, values);
    if (!result.valid) {
      setErrors(result.errors);
      // Mark everything touched so every message is allowed to show, not just
      // the fields the user happened to visit.
      setTouched(Object.fromEntries(Object.keys(values).map((field) => [field, true])));
      return;
    }

    setErrors({});
    setSubmitting(true);
    try {
      // The validated, coerced and stripped value is submitted — not the raw
      // form state, which may still carry untrimmed strings.
      await onSubmit?.(result.value);
    } finally {
      setSubmitting(false);
    }
  }, [onSubmit, schema, values]);

  const reset = useCallback(() => {
    submitAttempted.current = false;
    setValues(defaults);
    setErrors({});
    setTouched({});
  }, [defaults]);

  /** An error is only shown once the field has been touched or a submit was tried. */
  const errorFor = useCallback(
    (name) => (touched[name] || submitAttempted.current ? errors[name] : undefined),
    [errors, touched]
  );

  const fieldProps = useCallback(
    (name) => {
      const message = errorFor(name);
      const id = `${baseId}-${name}`;

      return {
        id,
        name,
        value: values[name] ?? '',
        onChange: handleChange,
        onBlur: handleBlur,
        invalid: Boolean(message),
        // Points at the error element only when one exists. A dangling
        // aria-describedby makes some screen readers announce nothing at all.
        describedBy: message ? `${id}-error` : undefined,
        error: message,
        errorId: `${id}-error`
      };
    },
    [baseId, errorFor, handleBlur, handleChange, values]
  );

  return {
    values,
    errors,
    touched,
    submitting,
    fieldProps,
    setFieldValue,
    setValues,
    handleChange,
    handleBlur,
    handleSubmit,
    reset
  };
}
